import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Send, Sparkles, X } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { sanitize } from '@/lib/html'
import { cn } from '@/lib/cn'

/**
 * Floating AI Guide chat widget. Triggered by the sparkles icon in the
 * header. Calls POST /api/chatbot/guide (BFF → FastAPI /guide-chatbot),
 * sanitises replies with DOMPurify, and delegates anchor clicks to
 * React Router so same-origin links navigate inside the SPA.
 */

type Role = 'user' | 'assistant'
type Message = { role: Role; content: string }

interface Props {
  open: boolean
  onClose: () => void
}

const INTRO_HTML =
  "Hi! I'm your BoKapSys guide. Ask me anything about the app - " +
  'for example, <i>“How do I upload a statement?”</i>, <i>“How do AI agents work?”</i>, ' +
  'or <i>“How do I invite a team member?”</i>'

export function GuideChatWidget({ open, onClose }: Props) {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [sending, setSending] = useState(false)
  const messagesRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 250)
      return () => window.clearTimeout(id)
    }
  }, [open])

  useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages, sending])

  const send = async () => {
    const text = input.trim()
    if (!text || sending) return
    const history = messages.slice(-10)
    setMessages((m) => [...m, { role: 'user', content: text }])
    setInput('')
    setSending(true)
    try {
      const data = await api.post<{ reply?: string }>('/api/chatbot/guide', {
        message: text,
        history,
      })
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: data.reply || "Sorry, I couldn't answer that." },
      ])
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 402
          ? 'Your tenant has reached its monthly AI token limit. Please upgrade your subscription.'
          : 'I had trouble reaching the guide service. Please try again.'
      setMessages((m) => [...m, { role: 'assistant', content: msg }])
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  // Delegated click handler - any <a href="/..."> inside a bot reply is
  // routed through React Router instead of a full reload.
  const onReplyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest('a')
    if (!target) return
    const href = target.getAttribute('href') || ''
    if (!href.startsWith('/') || href.startsWith('//')) return
    e.preventDefault()
    navigate(href)
    onClose()
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-label="AI Guide"
      className="fixed bottom-6 right-6 z-50 flex h-[540px] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl"
    >
      <div className="flex items-center justify-between gap-2 bg-primary-gradient px-4 py-3 text-primary-foreground">
        <div className="flex items-center gap-2 font-semibold">
          <Sparkles className="size-4" />
          BoKapSys Guide
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close guide"
          className="flex size-7 items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
        >
          <X className="size-4" />
        </button>
      </div>

      <div
        ref={messagesRef}
        onClick={onReplyClick}
        className="flex flex-1 flex-col gap-3 overflow-y-auto bg-muted/40 p-4 text-sm"
      >
        <BotBubble html={INTRO_HTML} />
        {messages.map((m, i) =>
          m.role === 'user' ? (
            <UserBubble key={i}>{m.content}</UserBubble>
          ) : (
            <BotBubble key={i} html={m.content} />
          ),
        )}
        {sending ? <BotBubble html="<span class='opacity-60'>Thinking…</span>" /> : null}
      </div>

      <div className="flex items-center gap-2 border-t bg-card p-3">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about any feature…"
          disabled={sending}
          className="flex-1 rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={sending || !input.trim()}
          aria-label="Send"
          className="flex size-9 items-center justify-center rounded-md bg-primary-gradient text-primary-foreground shadow-btn-primary hover:brightness-105 disabled:opacity-50"
        >
          <Send className="size-4" />
        </button>
      </div>
    </div>
  )
}

function UserBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-primary-foreground">
      {children}
    </div>
  )
}

function BotBubble({ html }: { html: string }) {
  return (
    <div
      className={cn(
        'mr-auto max-w-[90%] rounded-2xl rounded-bl-sm border bg-card px-3 py-2 text-foreground',
        '[&_a]:font-semibold [&_a]:text-primary [&_a]:underline',
        '[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
        '[&_li]:mb-1',
      )}
      // eslint-disable-next-line react/no-danger -- sanitised via DOMPurify (lib/html.ts)
      dangerouslySetInnerHTML={{ __html: sanitize(html) }}
    />
  )
}
